import {
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  CalendarDaysIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  FlagIcon,
  LinkIcon,
  PlusIcon,
  RectangleStackIcon,
  TrashIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, isStatus, errorMessage } from "@/lib/api";
import { useProject } from "@/lib/project";
import { useEpics, useSprints, useTask } from "@/lib/queries";
import type { Task } from "@/lib/types";
import { formatDateTime, formatDuration, taskKey } from "@/lib/utils";
import { Discussion } from "../collab/Discussion";
import { EntityFiles } from "../files/Files";
import { EditableTitle, EntityBody } from "../page";
import { AddPropertyButton, CustomPropertyRows, PropertyRow, ValueButton } from "../properties/Properties";
import { ErrorState, PageSpinner, StageBadge } from "../ui";
import { useFeedback } from "../ui/feedback";
import { MenuDivider, MenuItem, Popover } from "../ui/Popover";
import { AssigneesRowValue, TimeTracking } from "./Assignees";
import { useNow, useTaskActions } from "./hooks";
import { EpicPicker, SprintPicker, StagePicker } from "./pickers";

function PRLinks({ task }: { task: Task }) {
  const { pid, canEdit } = useProject();
  const { run } = useTaskActions();
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");

  const add = async () => {
    const v = url.trim();
    if (!v) return;
    await run(api.addPRLink(pid, task.id, v));
    setUrl("");
    setAdding(false);
  };

  return (
    <div className="flex min-h-[34px] flex-col justify-center gap-0.5 px-2 py-1 text-sm">
      {task.prLinks.map((l) => (
        <div key={l.id} className="group flex items-center gap-1.5">
          <a href={l.url} target="_blank" rel="noreferrer" className="truncate text-accent hover:underline">
            {l.url.replace(/^https?:\/\/(www\.)?/, "")}
          </a>
          {canEdit && (
            <button type="button" onClick={() => run(api.deletePRLink(pid, task.id, l.id))} className="text-faint opacity-0 group-hover:opacity-100 hover:text-fg" aria-label="Remove link">
              <XMarkIcon className="size-3.5" />
            </button>
          )}
        </div>
      ))}
      {!task.prLinks.length && !adding && <span className="text-faint">Empty</span>}
      {canEdit &&
        (adding ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
            className="flex gap-1"
          >
            <input
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => !url && setAdding(false)}
              onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), setAdding(false))}
              placeholder="https://github.com/org/repo/pull/123"
              className="field py-1"
            />
          </form>
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="flex w-fit items-center gap-1 text-xs text-faint hover:text-muted">
            <PlusIcon className="size-3.5" /> Add PR link
          </button>
        ))}
    </div>
  );
}

function ReadOnlyValue({ children, empty }: { children?: React.ReactNode; empty?: boolean }) {
  return (
    <div className="flex min-h-[34px] items-center px-2 py-1 text-sm" title="Set automatically">
      {empty ? <span className="text-faint">Empty</span> : children}
    </div>
  );
}

function TaskFields({ task }: { task: Task }) {
  const { pid, stageById } = useProject();
  const { update } = useTaskActions();
  const epics = useEpics(pid);
  const sprints = useSprints(pid, task.epicId);
  const stage = stageById.get(task.stageId);
  const epic = epics.data?.find((e) => e.id === task.epicId);
  const sprint = sprints.data?.find((s) => s.id === task.sprintId);
  const toQA = task.inProgressAt && task.qaReachedAt ? (new Date(task.qaReachedAt).getTime() - new Date(task.inProgressAt).getTime()) / 1000 : null;
  const now = useNow(30_000, !!task.inProgressAt && !task.qaReachedAt);

  return (
    <div className="space-y-px">
      <PropertyRow icon={ArrowPathIcon} label="Stage">
        <StagePicker
          value={task.stageId}
          onChange={(stageId) => update(task.id, { stageId })}
          trigger={(t) => (
            <ValueButton ref={t.ref} onClick={t.onClick}>
              {stage && <StageBadge color={stage.color}>{stage.name}</StageBadge>}
            </ValueButton>
          )}
        />
      </PropertyRow>
      <PropertyRow icon={UsersIcon} label="Assignees">
        <AssigneesRowValue task={task} />
      </PropertyRow>
      <PropertyRow icon={FlagIcon} label="Epic">
        <EpicPicker
          value={task.epicId}
          onChange={(epicId) => update(task.id, { epicId, sprintId: null })}
          trigger={(t) => (
            <ValueButton ref={t.ref} onClick={t.onClick}>
              <span dir="auto">{epic?.title ?? "…"}</span>
            </ValueButton>
          )}
        />
      </PropertyRow>
      <PropertyRow icon={RectangleStackIcon} label="Sprint">
        <SprintPicker
          epicId={task.epicId}
          value={task.sprintId}
          onChange={(sprintId) => update(task.id, { sprintId })}
          trigger={(t) => (
            <ValueButton ref={t.ref} onClick={t.onClick}>
              {task.sprintId ? <span dir="auto">{sprint?.title ?? "…"}</span> : <span className="text-muted">Backlog</span>}
            </ValueButton>
          )}
        />
      </PropertyRow>
      <PropertyRow icon={CalendarDaysIcon} label="In Progress started at">
        <ReadOnlyValue empty={!task.inProgressAt}>{formatDateTime(task.inProgressAt)}</ReadOnlyValue>
      </PropertyRow>
      <PropertyRow icon={ClockIcon} label="In Progress → QA">
        <ReadOnlyValue empty={!task.inProgressAt}>
          {toQA !== null ? (
            formatDuration(toQA)
          ) : (
            <span className="text-muted">{formatDuration((now - new Date(task.inProgressAt ?? now).getTime()) / 1000)} so far</span>
          )}
        </ReadOnlyValue>
      </PropertyRow>
      <PropertyRow icon={CalendarDaysIcon} label="QA reached at">
        <ReadOnlyValue empty={!task.qaReachedAt}>{formatDateTime(task.qaReachedAt)}</ReadOnlyValue>
      </PropertyRow>
      <PropertyRow icon={LinkIcon} label="PR Links">
        <PRLinks task={task} />
      </PropertyRow>
      <CustomPropertyRows entityType="task" entityId={task.id} values={task.values} />
      <AddPropertyButton entityType="task" />
    </div>
  );
}

export function TaskDetail({ taskId, mode, onClose }: { taskId: string; mode: "peek" | "page"; onClose?: () => void }) {
  const { pid, bundle, canEdit } = useProject();
  const task = useTask(pid, taskId);
  const { update, run } = useTaskActions();
  const { confirm, toast } = useFeedback();
  const navigate = useNavigate();

  if (task.isPending) return <PageSpinner />;
  if (task.error) {
    return <ErrorState message={isStatus(task.error, 404) ? "This task was deleted or doesn't exist." : errorMessage(task.error)} />;
  }
  const t = task.data;
  const key = taskKey(bundle.project.key, t.number);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="rounded bg-hover px-1.5 py-0.5 font-mono text-xs text-muted">{key}</span>
        <div className="ms-auto flex items-center gap-1">
          {mode === "peek" && (
            <Link to={`/p/${pid}/tasks/${t.id}`} className="rounded p-1.5 text-muted hover:bg-hover hover:text-fg" title="Open as full page">
              <ArrowsPointingOutIcon className="size-4" />
            </Link>
          )}
          <Popover
            placement="bottom-end"
            className="w-48 p-1"
            trigger={(tr) => (
              <button ref={tr.ref} onClick={tr.onClick} className="rounded p-1.5 text-muted hover:bg-hover hover:text-fg" aria-label="More">
                <EllipsisHorizontalIcon className="size-4" />
              </button>
            )}
          >
            {(close) => (
              <>
                <MenuItem
                  icon={LinkIcon}
                  onClick={() => {
                    close();
                    navigator.clipboard?.writeText(`${window.location.origin}/p/${pid}/tasks/${t.id}`);
                    toast("Link copied", "success");
                  }}
                >
                  Copy link
                </MenuItem>
                {canEdit && (
                  <>
                    <MenuDivider />
                    <MenuItem
                      icon={TrashIcon}
                      danger
                      onClick={async () => {
                        close();
                        const ok = await confirm({
                          title: `Delete ${key}?`,
                          message: "The task, its comments, history and time log will be permanently deleted.",
                          confirmLabel: "Delete task",
                          danger: true,
                        });
                        if (!ok) return;
                        await run(api.deleteTask(pid, t.id));
                        if (mode === "peek") onClose?.();
                        else navigate(`/p/${pid}/tasks`);
                      }}
                    >
                      Delete task
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </Popover>
          {mode === "peek" && (
            <button type="button" onClick={onClose} className="rounded p-1.5 text-muted hover:bg-hover hover:text-fg" aria-label="Close">
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>
      </div>

      <EditableTitle value={t.title} editable={canEdit} onSave={(title) => update(t.id, { title })} className="mb-4 text-[28px]" />
      <TaskFields task={t} />

      <div className="my-6">
        <TimeTracking task={t} />
      </div>

      <div className="border-t border-line pt-8">
        <EntityBody entityId={t.id} title={t.title} initial={t.body} save={(body) => api.updateTask(pid, t.id, { body })} />
      </div>

      <div className="mt-10 space-y-10 border-t border-line pt-6">
        <EntityFiles entityType="task" entityId={t.id} title={t.title} />
        <Discussion entityId={t.id} />
      </div>
    </div>
  );
}
